import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import { formatSaDate, type ComplianceKey } from '@claripick/shared';
// Auth only. This page used to import Firestore (for one write at sign-up),
// and with it the database SDK, its persistent cache, Storage and Functions,
// so a phone downloaded and booted all of them before it could take a
// password. Firestore now loads at the one moment it is needed.
import { auth } from '@/lib/auth';
import { describeFirestoreError } from '@/lib/errors';
import { registerServiceWorkerSoon } from '@/lib/sw';
import { Button, Field, Input, cx } from '@/components';

/** Every date, figure, reference and section sits in the record face. */
const REC = 'record font-mono tabular';

type Mode = 'signin' | 'signup' | 'reset';

const HEADING: Record<Mode, string> = {
  signin: 'Sign in',
  signup: 'Create your account',
  reset: 'Reset your password',
};

const PREVIEW_KEYS: ComplianceKey[] = [
  'dsd_financial_statements',
  'companies_act_afs',
  'dsd_annual_report',
  'cipc_annual_return',
];

interface PreviewRow {
  key: ComplianceKey;
  title: string;
  citation: string;
  dueDate: string;
}

/**
 * The four lines a February year-end NPC sees first. Computed by the same
 * engine the app runs, so the preview is the product and never drifts
 * from it. Static: this page has no profile to read.
 */
async function computePreview(): Promise<{ rows: PreviewRow[]; total: number }> {
  const [{ generateComplianceDeadlines }, { COMPLIANCE_RULES }] = await Promise.all([
    import('@claripick/shared/compliance/engine'),
    import('@claripick/shared/compliance/rules'),
  ]);
  const rows = generateComplianceDeadlines(
    { fyEndMonth: 2, legalForm: 'npc', cipcIncorporationDate: '2016-05-19', hasPboApproval: true },
    new Date(),
  );
  // Sorted by the date, not by the order the four keys happen to be
  // listed in above. This is a register: a reader takes the top line as
  // the next thing due, and the old order printed the February
  // statements after the May annual return. ISO dates sort lexically.
  const picked = PREVIEW_KEYS.map((key) => rows.find((r) => r.complianceKey === key))
    .filter((r): r is NonNullable<typeof r> => Boolean(r))
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
    .map((r) => ({
      key: r.complianceKey,
      title: COMPLIANCE_RULES[r.complianceKey].title,
      citation: COMPLIANCE_RULES[r.complianceKey].citation,
      dueDate: r.dueDate,
    }));
  return { rows: picked, total: rows.length };
}

/**
 * The preview, fetched after the page has painted.
 *
 * It used to be computed during the first render, which put the compliance
 * engine, its rules and the whole shared library in front of the sign-in
 * form on every device. It sits in the band, which only shows from `md`
 * up, so a phone never draws it and now never downloads it; a wider screen
 * gets it a moment after the form, into a frame already the height it
 * will fill, so nothing on the page moves when it arrives.
 */
function usePreviewRegister(): { rows: PreviewRow[]; total: number } | null {
  const [preview, setPreview] = useState<{ rows: PreviewRow[]; total: number } | null>(null);
  useEffect(() => {
    const wide = window.matchMedia('(min-width: 768px)');
    let cancelled = false;
    let started = false;
    const load = () => {
      if (started || !wide.matches) return;
      started = true;
      window.requestAnimationFrame(() =>
        window.setTimeout(() => {
          computePreview()
            .then((p) => {
              if (!cancelled) setPreview(p);
            })
            .catch(() => {
              // The band reads without it; the claim and the closing line stay.
            });
        }, 0),
      );
    };
    load();
    wide.addEventListener?.('change', load);
    return () => {
      cancelled = true;
      wide.removeEventListener?.('change', load);
    };
  }, []);
  return preview;
}

export function AuthPage() {
  const [searchParams] = useSearchParams();
  // Arriving from "Create your workspace" should open account creation, not
  // a sign-in form with a create link hidden underneath it.
  //
  // `next` carries the visitor's pricing-page choice through sign-up
  // (`/onboarding?plan=pro`), and it comes off the URL, so it is only
  // honoured when it is a path on this origin. A single leading slash, and
  // never a second one: `//evil.example` is protocol-relative and would
  // send somebody who just typed their password to another site.
  const raw = searchParams.get('next');
  const next = raw && raw.startsWith('/') && !raw.startsWith('//') ? raw : null;
  // /signup is the address people type; it opens account creation too.
  const { pathname } = useLocation();
  const [mode, setMode] = useState<Mode>(searchParams.get('mode') === 'signup' || pathname === '/signup' ? 'signup' : 'signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const errorRef = useRef<HTMLParagraphElement>(null);
  const navigate = useNavigate();
  // A refusal lands under the password field, which on a phone with the
  // keyboard up is below what is showing. Focus moves to it, which also
  // scrolls it into view and has a screen reader read it.
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);
  const preview = usePreviewRegister();
  // Nothing on this page waits on data, so once it has painted and gone
  // idle it fetches what a sign-in leads to (the shell, with Firestore)
  // while the person types, and then lets the service worker start its
  // precache, which finds those files already here.
  useEffect(() => {
    const ahead = () => {
      void import('../shell/AppShell').catch(() => undefined).finally(registerServiceWorkerSoon);
    };
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(ahead, { timeout: 4_000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(ahead, 1_500);
    return () => window.clearTimeout(id);
  }, []);

  function go(to: Mode) {
    setMode(to);
    setError(null);
    setSentTo(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'reset') {
        try {
          await sendPasswordResetEmail(auth, email);
        } catch (err) {
          // Anything except a malformed address or a rate limit is
          // answered identically. Saying "no account with that email"
          // tells a stranger which of an organisation's addresses are
          // registered, and this workspace holds donor and banking data.
          const code = (err as { code?: string })?.code ?? '';
          if (code === 'auth/invalid-email' || code === 'auth/too-many-requests') throw err;
        }
        setSentTo(email);
        return;
      }
      if (mode === 'signup') {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(cred.user, { displayName: name });
        // PII lives in Firestore (africa-south1), not in Auth.
        const [{ db }, { doc, setDoc }] = await Promise.all([
          import('@/lib/firebase'),
          import('firebase/firestore'),
        ]);
        await setDoc(doc(db, `users/${cred.user.uid}`), {
          displayName: name,
          email,
          locale: 'en-ZA',
          claimsVersion: 0,
          channelPrefs: { email: true, whatsapp: false, weeklyDigest: true },
        });
        // createOrg reads email_verified to decide whether this address may
        // claim its domain for workspace discovery, and an invite locked to
        // an address cannot be checked against an unverified one. Nothing
        // sent the mail, so no password account was ever verified. Failure
        // is not fatal to sign-up: the account exists and the address can
        // be verified later from Settings.
        void sendEmailVerification(cred.user).catch(() => undefined);
        navigate(next || '/onboarding');
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        navigate(next || '/');
      }
    } catch (err) {
      setError(describeFirestoreError(err));
    } finally {
      setBusy(false);
    }
  }

  // A grant maker arrives from the website's Grant makers page or the demo's
  // giving side with ?plan=fund on the onboarding address. The page used to
  // greet them with the NPO's DSD deadline and send them to the NPO demo.
  const givesGrants = /[?&]plan=fund(?:&|$)/.test(next ?? '');
  const lead = givesGrants
    ? 'Run your calls, reviews, board decisions and payments to the organisations you fund in one place. Create your account, then choose We give grants.'
    : 'Your DSD annual report is due nine months after your financial year-end. Enter yours and the rest is on the calendar in three minutes.';
  const demoTo = givesGrants ? '/demo?view=grant-makers' : '/demo';
  const demoWhat = givesGrants
    ? ', a sample foundation with its call, applications and payments, no account needed.'
    : ', a Tembisa NPC with its statutory calendar and its grants, no account needed.';

  return (
    <div className="min-h-screen bg-paper md:grid md:grid-cols-12">
      {/* The band: the product's own output for a February year-end,
          printed in pale ink on the deep green. Not a picture of it.

          On a phone it was 480px tall: the eyebrow and the claim set as a
          nine-line heading, so the Sign in button was below the fold for
          the returning user who is nearly every visit. Below 768px signing
          in gets the logo and one line, about 110px; creating an account
          keeps the claim, at 20px. */}
      <section
        className={cx(
          'bg-forest-ink px-4 text-white sm:px-5 md:col-span-7 md:flex md:min-h-screen md:flex-col md:px-12 md:py-10 lg:px-16',
          mode === 'signup' ? 'py-5' : 'py-3',
        )}
        aria-label="What Claripick does"
      >
        <a href="https://claripick.com" className="-my-1 flex min-h-tap w-max items-center gap-3" aria-label="Back to claripick.com">
          <img src="/logo.svg" alt="" className="h-8 w-8 shrink-0 brightness-0 invert" />
          <span className="font-display text-lg font-semibold tracking-[-0.03em] text-white">Claripick</span>
        </a>
        <div className="md:my-auto md:py-12">
          {/* Not uppercase mono: the direction retired that eyebrow by name
              ("a technical-brutalist tic", and barely legible at 11px). The
              record face is for the record. */}
          <p className="hidden text-sm font-semibold tracking-[-0.01em] text-forest-pale md:block">Grant management and statutory compliance, South Africa</p>
          {mode !== 'signup' ? (
            <p className="mt-1 text-sm text-forest-pale md:hidden">Every statutory date, with its section of the Act.</p>
          ) : null}
          <h1
            className={cx(
              'font-display font-semibold tracking-[-0.025em] text-white md:mt-5 md:max-w-[22ch] md:text-5xl md:leading-[1.02]',
              mode === 'signup' ? 'mt-3 text-xl leading-tight' : 'sr-only md:not-sr-only',
            )}
            style={{ textWrap: 'balance' }}
          >
            {lead}
          </h1>
          <div className="mt-10 hidden md:block">
            {/* The frame is the height the count line and four rows fill at
                each width, so the band, which is centred, does not move when
                they arrive. Measured with the faces loaded; min-height, so
                larger text grows it rather than cutting a row off. */}
            <div
              className="min-h-[624px] min-[820px]:min-h-[556px] min-[900px]:min-h-[536px] lg:min-h-[516px] min-[1100px]:min-h-[492px] min-[1152px]:min-h-[468px] min-[1180px]:min-h-[420px] min-[1200px]:min-h-[398px] min-[1536px]:min-h-[376px]"
              aria-busy={preview === null}
            >
              {preview ? (
                <>
                  <p className="text-sm text-white/65">
                    Non-profit company, financial year ending 28 February. Four of the{' '}
                    <span className={REC}>{preview.total}</span> dates the engine writes, computed today.
                  </p>
                  <ul className="mt-3 max-w-2xl border-t border-white/60">
                    {preview.rows.map((row) => (
                      <li key={row.key} className="flex items-start gap-4 border-b border-white/15 py-3.5">
                        <div className="min-w-0 flex-1">
                          <p className="text-base font-semibold text-white">{row.title}</p>
                          <p className={cx(REC, 'mt-1 text-xs text-white/60')}>{row.citation}</p>
                        </div>
                        <span className={cx(REC, 'shrink-0 text-right text-base font-medium text-forest-pale')}>
                          {formatSaDate(row.dueDate)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                // Ruled lines where the register will print, nothing else.
                <ul className="mt-[60px] max-w-2xl border-t border-white/60" aria-hidden>
                  {PREVIEW_KEYS.map((key) => (
                    <li key={key} className="h-[78px] border-b border-white/15" />
                  ))}
                </ul>
              )}
            </div>
            <p className="mt-4 max-w-[60ch] text-sm text-white/65">
              Miss the DSD report and the Directorate issues a section 20 notice, then cancels the
              registration under section 21. Your data is stored in Firestore africa-south1
              (Johannesburg) and stays there.
            </p>
          </div>
        </div>
        <p className="mt-8 hidden text-xs text-white/50 md:block">
          <Link to={demoTo} className="font-semibold text-forest-pale underline underline-offset-2">Open the demo workspace</Link>{demoWhat}
        </p>
      </section>

      <div className="flex items-start px-4 py-5 sm:px-5 md:col-span-5 md:min-h-screen md:items-center md:px-10 md:py-10 lg:px-16">
        <section className="mx-auto w-full max-w-md md:mx-0" aria-label={HEADING[mode]}>
          <div className="rounded-ct-lg border border-line bg-paper-card p-5 shadow-dialog sm:p-8">
            <h2 className="font-display text-2xl font-semibold leading-tight tracking-[-0.02em] text-ink">
              {HEADING[mode]}
            </h2>
            {mode === 'reset' && !sentTo && (
              <p className="mt-4 text-sm text-ink-2">
                Enter the address you sign in with and we will email you a link to set a new
                password. The link works once and expires in an hour.
              </p>
            )}
            {sentTo ? (
              <div className="mt-6">
                <p role="status" className="rounded-ct-sm border border-line bg-forest-whisper px-3 py-3 text-sm text-ink">
                  If <span className={cx(REC, 'font-medium')}>{sentTo}</span> has an account, a reset
                  link is on its way. Check the spam folder before asking us: reset mail lands there
                  more often than it should.
                </p>
                <Button variant="secondary" block className="mt-4" onClick={() => go('signin')}>
                  Back to sign in
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-5 space-y-4 sm:mt-6">
                {mode === 'signup' && (
                  <Field label="Your name" required>
                    <Input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required />
                  </Field>
                )}
                <Field label="Email" required>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </Field>
                {mode !== 'reset' && (
                  <Field label="Password" hint="At least 8 characters." required>
                    <Input
                      type="password"
                      value={password}
                      minLength={8}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                      required
                    />
                  </Field>
                )}
                {error && (
                  <p
                    ref={errorRef}
                    tabIndex={-1}
                    role="alert"
                    className="scroll-my-24 rounded-ct-sm border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger outline-none"
                  >
                    {error}
                  </p>
                )}
                <Button type="submit" variant="primary" block loading={busy}>
                  {mode === 'signup' ? 'Create account' : mode === 'reset' ? 'Email me a reset link' : 'Sign in'}
                </Button>
                {/* A locked-out user has no other way back in: there is no
                    support desk on the free tier and the workspace is the
                    organisation's statutory register. */}
                <p className="-my-1 text-center text-sm">
                  {mode === 'reset' ? (
                    <button
                      type="button"
                      className="min-h-tap font-semibold text-forest underline underline-offset-2"
                      onClick={() => go('signin')}
                    >
                      Back to sign in
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="min-h-tap text-ink-3 underline underline-offset-2 hover:text-ink"
                      onClick={() => go('reset')}
                    >
                      Forgotten your password?
                    </button>
                  )}
                </p>
              </form>
            )}
            {/* The other way in, as a button of its own under the form. It
                was a text link beside the heading that wrapped to two lines
                ("I have an / account") on a phone. */}
            {mode !== 'reset' && !sentTo ? (
              <Button variant="secondary" block className="mt-4" onClick={() => go(mode === 'signup' ? 'signin' : 'signup')}>
                {mode === 'signup' ? 'I already have an account' : 'Create an account'}
              </Button>
            ) : null}
            <p className="mt-5 border-t border-line pt-4 text-xs text-ink-3">
              Only looking?{' '}
              <Link to={demoTo} className="font-semibold text-forest underline underline-offset-2">
                Explore the demo workspace
              </Link>
              {demoWhat}
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
